import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock } from 'lucide-react';
import {
  Crown, Coins, TrendingUp, Boxes,
  ShieldAlert, Radar, MessageSquare, Truck,
  Sparkles, BookOpen, Building2, Database,
} from 'lucide-react';
import { useState } from 'react';

const PUCKS = [
  { id: 'ceo',         label: 'AI CEO',        Icon: Crown,          accent: '#8B5CF6', route: '/home' },
  { id: 'cfo',         label: 'AI CFO',        Icon: Coins,          accent: '#14B8A6', route: '/insights' },
  { id: 'sales',       label: 'AI Sales',      Icon: TrendingUp,     accent: '#F59E0B', route: '/sales-strategy' },
  { id: 'inventory',   label: 'AI Inventory',  Icon: Boxes,          accent: '#6366F1', route: '/inventory' },
  { id: 'fraud',       label: 'AI Fraud',      Icon: ShieldAlert,    accent: '#EF4444', route: '/managers/fraud' },
  { id: 'market',      label: 'AI Market',     Icon: Radar,          accent: '#06B6D4', route: '/managers/market' },
  { id: 'reactive',    label: 'AI Reactive',   Icon: MessageSquare,  accent: '#EC4899', route: '/managers/reactive' },
  { id: 'purchase',    label: 'AI Purchase',   Icon: Truck,          accent: '#10B981', route: '/managers/purchase' },
  { id: 'proactive',   label: 'AI Proactive',  Icon: Sparkles,       accent: '#38BDF8', route: '/managers/proactive' },
  { id: 'erp',         label: 'Smart ERP',     Icon: BookOpen,       accent: '#2563EB', route: '/erp' },
  { id: 'multibranch', label: 'Multi-Branch',  Icon: Building2,      accent: '#F43F5E', route: '/multi-branch' },
  { id: 'bridge',      label: 'Data Bridge',   Icon: Database,       accent: '#84CC16', route: '/integrations' },
];

export default function ManagerGrid() {
  const navigate = useNavigate();
  const { hasPlan } = useAuth();
  const isPremium = hasPlan('premium');
  const [upsellOpen, setUpsellOpen] = useState(false);

  const handleClick = (m) => {
    if (!isPremium) {
      setUpsellOpen(true);
      return;
    }
    navigate(m.route);
  };

  return (
    <>
      <div
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 w-full"
        data-testid="home-manager-grid"
      >
        {PUCKS.map(m => (
          <button
            key={m.id}
            onClick={() => handleClick(m)}
            data-testid={`hub-puck-${m.id}`}
            className="relative rounded-3xl overflow-hidden transition-all hover:scale-[1.03] hover:brightness-110 active:scale-95 group"
            style={{
              background: `linear-gradient(135deg, ${m.accent}15, ${m.accent}08)`,
              border: `1px solid ${m.accent}25`,
            }}
          >
            {!isPremium && (
              <div
                className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-black/50 border border-white/30 flex items-center justify-center"
                data-testid={`hub-puck-lock-${m.id}`}
              >
                <Lock size={10} className="text-white" />
              </div>
            )}
            <div className={`relative z-[2] flex flex-col items-center justify-center py-5 px-2 gap-2 ${!isPremium ? 'opacity-75' : ''}`}>
              <m.Icon size={32} style={{ color: m.accent }} strokeWidth={1.6} />
              <span
                className="text-white text-[12px] font-semibold leading-tight tracking-tight text-center truncate w-full"
                data-testid={`hub-puck-label-${m.id}`}
              >
                {m.label}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Premium Upsell Modal */}
      {upsellOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="premium-upsell-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setUpsellOpen(false)} data-testid="premium-upsell-backdrop" />
          <div className="relative bg-white rounded-3xl max-w-sm mx-4 overflow-hidden shadow-2xl animate-slide-up">
            <div className="h-28 bg-gradient-to-br from-brand-purple to-brand-purple-deep flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Lock size={28} className="text-white" />
              </div>
            </div>
            <div className="p-6 text-center">
              <h3 className="text-gray-900 font-extrabold text-lg" data-testid="upsell-title">Premium Feature</h3>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                Unlock the full 12-Manager Hub — strategic insights, proactive alerts, ERP, multi-branch view, and integrations.
              </p>
              <button
                data-testid="upsell-upgrade-btn"
                onClick={() => { setUpsellOpen(false); navigate('/billing'); }}
                className="mt-5 w-full h-12 rounded-xl bg-gradient-to-r from-brand-purple via-brand-purple-soft to-purple-700 text-white font-bold shadow-accent hover:opacity-90 transition-opacity"
              >
                Start 14-day Free Trial
              </button>
              <button
                onClick={() => setUpsellOpen(false)}
                data-testid="upsell-close-btn"
                className="mt-2 text-gray-400 text-sm hover:text-gray-600"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
